import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Files, CheckCircle, Warning, XCircle, Clock, UploadSimple, Trash, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: 'kbis', label: 'Kbis (extrait -3 mois)' },
  { value: 'assurance_rc_pro', label: 'Assurance RC Professionnelle' },
  { value: 'assurance_decennale', label: 'Assurance Décennale' },
  { value: 'assurance_biennale', label: 'Assurance Biennale' },
  { value: 'attestation_fiscale', label: 'Attestation fiscale (Impôts)' },
  { value: 'attestation_urssaf', label: 'Attestation URSSAF' },
  { value: 'qualification', label: 'Qualification (Qualibat, RGE…)' },
  { value: 'autre', label: 'Autre document' },
];

const BADGE_CONFIG = {
  valide: { label: 'Valide', icon: CheckCircle, color: '#2D6A4F', bg: '#E8F5E9' },
  expire_bientot: { label: 'Expire bientôt', icon: Warning, color: '#B8860B', bg: '#FFF8E1' },
  expiree: { label: 'Expirée', icon: XCircle, color: '#8B3A3A', bg: '#FFEBEE' },
  non_verifie: { label: 'Non vérifiée', icon: Clock, color: '#6B6B6B', bg: '#F5F5F3' },
};

export default function MesDocumentsEntreprise({ entrepriseId: overrideId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('kbis');
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const params = overrideId ? { entreprise_id: overrideId } : {};
      const res = await axios.get(`${API_URL}/api/entreprise/documents`, { params, withCredentials: true });
      setDocs(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [overrideId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type_document', selectedType);
      const url = overrideId
        ? `${API_URL}/api/entreprise/documents/upload?entreprise_id=${overrideId}`
        : `${API_URL}/api/entreprise/documents/upload`;
      await axios.post(url, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploadé. Analyse OCR en cours...');
      fileRef.current && (fileRef.current.value = '');
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/api/entreprise/documents/${id}`, { withCredentials: true });
      toast.success('Supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownload = (d) => {
    window.open(`${API_URL}/api/entreprise/documents/${d.id}/download`, '_blank');
  };

  // Group by type
  const byType = docs.reduce((acc, d) => {
    if (!acc[d.type]) acc[d.type] = [];
    acc[d.type].push(d);
    return acc;
  }, {});

  return (
    <div data-testid="mes-documents-entreprise">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Files size={18} weight="bold" className="text-[#C4773B]" />
          <h2 className="text-base font-medium text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Documents administratifs</h2>
          <span className="text-xs text-[#6B6B6B]">({docs.length})</span>
        </div>
      </div>

      {/* Upload zone */}
      <div className="bg-[#FAFAF8] border border-[#E0E0DB] p-4 mb-5 flex flex-wrap items-center gap-3">
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="rounded-none flex-1 min-w-[180px] max-w-xs" data-testid="type-document-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <input type="file" ref={fileRef} onChange={handleUpload} className="hidden" data-testid="file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-2" data-testid="upload-doc-entreprise-btn">
          <UploadSimple size={16} weight="bold" />
          {uploading ? 'Envoi...' : 'Uploader le document'}
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-[#6B6B6B]">Chargement...</p>
      ) : docs.length === 0 ? (
        <div className="bg-white border border-[#E0E0DB] p-8 text-center text-sm text-[#6B6B6B] italic">
          Aucun document. Uploadez vos Kbis, attestations d'assurance, qualifications.
        </div>
      ) : (
        <div className="space-y-4">
          {TYPES.filter((t) => byType[t.value]).map((t) => (
            <div key={t.value}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#C4773B] mb-2">{t.label} ({byType[t.value].length})</h3>
              <div className="space-y-2">
                {byType[t.value].map((d) => {
                  const badge = BADGE_CONFIG[d.badge_validite] || BADGE_CONFIG.non_verifie;
                  const Icon = badge.icon;
                  const dateVal = d.donnees_extraites?.date_validite || d.donnees_extraites?.periode_fin;
                  return (
                    <div key={d.id} className="bg-white border border-[#E0E0DB] p-3 flex items-center justify-between gap-3" data-testid={`doc-entreprise-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Files size={14} className="text-[#6B6B6B]" />
                          <span className="font-medium text-[#2C2C2C] truncate">{d.name}</span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] border" style={{ borderColor: badge.color, color: badge.color, backgroundColor: badge.bg }}>
                            <Icon size={10} weight="fill" />
                            {badge.label}
                          </span>
                        </div>
                        <p className="text-[10px] text-[#6B6B6B]">
                          Uploadé le {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('fr-FR') : '—'} par {d.uploaded_by_name || '—'}
                          {dateVal && ` · Validité : ${dateVal}`}
                          {d.analyse_status === 'pending' && ' · Analyse OCR en cours...'}
                          {d.donnees_extraites?.numero_police && ` · N°${d.donnees_extraites.numero_police}`}
                          {d.donnees_extraites?.assureur && ` · ${d.donnees_extraites.assureur}`}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="rounded-none h-7 w-7 p-0" onClick={() => handleDownload(d)} title="Télécharger">
                          <DownloadSimple size={12} />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-none h-7 w-7 p-0 text-[#8B3A3A]" onClick={() => handleDelete(d.id)} title="Supprimer">
                          <Trash size={12} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
